// Build a tile-brush render manifest from an asset's extracted parts.
//
// A tile-brush asset exposes its tiles as a list of extracted parts, each
// `{ layer_name, file_url }` (the assets API shape). This is the single place
// that maps those FA-named layers onto the renderer's graph-theoretic role
// vocabulary (see docs/engineering/tile-brush-spec.md) with the right cell
// dimensions — so every consumer (the SPA preview, the render bench, and any
// downstream editor) assembles the same manifest instead of re-deriving the
// mapping. `drawTileBrush` then reads the result directly.

/**
 * @param {Array<{layer_name: string, file_url: string}>} layers
 *   The asset's extracted parts (name + image URL).
 * @param {object} [opts]
 * @param {string} [opts.id='preview']
 * @param {string} [opts.label='This brush']
 * @param {string} [opts.dir='']
 *   Prefix joined to relative tile files. Leave '' when `file_url`s are
 *   absolute (the CDN / fixture case) — `tileUrl` passes those through as-is.
 * @param {boolean} [opts.useStraightSeamConnector]
 *   The asset's stored seam-connector policy: `false` never overlays the
 *   Connector_A seam strip; `true`/`undefined` overlay it when present.
 * @returns {import('./tile-brushes.js').TileBrush}
 */
export function buildTileBrushFromLayers(layers, opts = {}) {
    const { id = 'preview', label = 'This brush', dir = '', useStraightSeamConnector } = opts;
    const byName = new Map((layers || []).map((l) => [l.layer_name, l]));
    const t = (name, cellsW, cellsH) => {
        const l = byName.get(name);
        return l ? { file: l.file_url, cellsW, cellsH } : null;
    };
    const list = (...xs) => xs.filter(Boolean);
    return {
        id,
        label,
        dir,
        useStraightSeamConnector,
        // Layer names are our shape vocabulary, so each role maps 1:1 — no per-tileset
        // variant/fallback heuristic. The FA-name divergence between wall sets is
        // resolved upstream (extraction / the fixture layer manifest).
        pieces: {
            straight: list(t('straight-3', 3, 1), t('straight-2', 2, 1), t('straight-1', 1, 1)),
            // Half-block that insets the run at a junction so the vertex piece sits
            // flush with no overlap (#54).
            straightHalf: list(t('straight-half', 1, 1)),
            // [0] default L, [1] rounded door-jamb (picked when door-adjacent).
            corner: list(t('corner-square', 1, 1), t('corner-rounded', 1, 1)),
            ending: list(t('straight-ending', 1, 1)),
            joint: list(t('t-junction', 1, 1)),
            connector: list(t('cross-junction', 1, 1)),
            // The cross-connector (inset cross) is rotated 45° to seam a diagonal
            // crossing. Absent in sets whose only cross is the edge-filling one.
            connectorDiagCross: list(t('cross-connector', 1, 1)),
            connectorDiag: list(t('diagonal-connector', 1, 1)),
            connectorStraight: list(t('straight-connector', 1, 1)),
        },
    };
}
